import { supabase } from './supabase';
import { HOME_ROUTE } from './homeRoute';

export const GOOGLE_AUTH_CALLBACK_PATH = '/auth/callback';

const PENDING_SIGNUP_KEY = 'seu.video.pending_signup';
const SIGNUP_TOKEN_KEY = 'seu.video.signup_token';

const SIGNUP_TOKEN_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type PendingGoogleSignup = {
  org_name: string;
};

export function googleCallbackUrl(): string {
  return `${window.location.origin}${GOOGLE_AUTH_CALLBACK_PATH}`;
}

export function getPendingGoogleSignup(): PendingGoogleSignup | null {
  try {
    const raw = sessionStorage.getItem(PENDING_SIGNUP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingGoogleSignup>;
    if (typeof parsed.org_name !== 'string') return null;
    const org_name = parsed.org_name.trim();
    if (!org_name) return null;
    return { org_name };
  } catch {
    return null;
  }
}

export function setPendingGoogleSignup(pending: PendingGoogleSignup): void {
  sessionStorage.setItem(PENDING_SIGNUP_KEY, JSON.stringify(pending));
}

export function clearPendingGoogleSignup(): void {
  sessionStorage.removeItem(PENDING_SIGNUP_KEY);
}

export function hasPendingGoogleSignup(): boolean {
  return getPendingGoogleSignup() !== null;
}

export function rememberSignupToken(token: string | null): void {
  if (!token || !SIGNUP_TOKEN_RE.test(token)) {
    sessionStorage.removeItem(SIGNUP_TOKEN_KEY);
    return;
  }
  sessionStorage.setItem(SIGNUP_TOKEN_KEY, token);
}

export function getSignupToken(): string | null {
  try {
    const raw = sessionStorage.getItem(SIGNUP_TOKEN_KEY);
    if (!raw || !SIGNUP_TOKEN_RE.test(raw)) return null;
    return raw;
  } catch {
    return null;
  }
}

export function clearSignupToken(): void {
  sessionStorage.removeItem(SIGNUP_TOKEN_KEY);
}

export function loggedInDestination(needsSetup: boolean): string {
  if (needsSetup) return '/setup-account';
  if (hasPendingGoogleSignup()) return GOOGLE_AUTH_CALLBACK_PATH;
  return HOME_ROUTE;
}

export type GoogleAuthErrorKind = 'canceled' | 'unavailable' | 'signup_closed' | 'generic';

export type MappedGoogleAuthError = {
  kind: GoogleAuthErrorKind;
  title: string;
  message: string;
};

export function mapGoogleAuthError(raw: string): MappedGoogleAuthError {
  const message = decodeURIComponent(raw.replace(/\+/g, ' ')).trim();
  const lower = message.toLowerCase();

  if (message.includes('signup_cap_reached')) {
    return {
      kind: 'signup_closed',
      title: 'Cadastro encerrado',
      message: 'O cadastro de novas organizações está encerrado. Se você já tem conta, faça login.',
    };
  }

  if (message.includes('signup_token_required') || message.includes('signup_token_invalid')) {
    return {
      kind: 'signup_closed',
      title: 'Link de cadastro necessário',
      message: 'O cadastro é feito por um link exclusivo. Peça um novo link se o seu já foi usado.',
    };
  }

  if (
    lower.includes('access_denied') ||
    lower.includes('user_cancelled') ||
    lower.includes('user_canceled') ||
    lower.includes('consent_required')
  ) {
    return {
      kind: 'canceled',
      title: 'Login cancelado',
      message: 'Você cancelou o acesso com Google. Pode tentar de novo quando quiser.',
    };
  }

  if (lower.includes('provider is not enabled') || lower.includes('unsupported provider')) {
    return {
      kind: 'unavailable',
      title: 'Google indisponível',
      message: 'Login com Google não está disponível no momento. Use e-mail e senha.',
    };
  }

  if (
    lower.includes('redirect_uri') ||
    lower.includes('invalid_client') ||
    lower.includes('unauthorized_client')
  ) {
    return {
      kind: 'unavailable',
      title: 'Configuração incompleta',
      message: 'O login com Google ainda não está configurado corretamente. Tente e-mail e senha.',
    };
  }

  return {
    kind: 'generic',
    title: 'Não foi possível entrar',
    message: 'Algo deu errado ao concluir o login com Google. Tente novamente.',
  };
}

export async function signInWithGoogle(signupToken?: string): Promise<{ error: Error | null }> {
  const redirectTo = new URL(googleCallbackUrl());
  if (signupToken && SIGNUP_TOKEN_RE.test(signupToken)) {
    redirectTo.searchParams.set('signup_token', signupToken);
  }
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: redirectTo.toString(),
      queryParams: { prompt: 'select_account' },
    },
  });
  return { error: error ? new Error(error.message) : null };
}
